/**
 * New request's calls, typed — `server/src/routes/requests.ts`.
 */
import type {
  BookRequestInput, BookRequestResult, CalendarView, CompareView, KnownContact, KnownProperty,
  NewRequestInput, NewRequestResult, Opening, ParsedTyped, PropertyCheck, Segment, WhenChip,
} from '@rowted/shared'
import { request } from '../lib/api.js'

export interface RequestSetup {
  today: string
  now: string
  /** The person's time zone: *started 9:41* is read in it. */
  timeZone: string
  when: WhenChip[]
  divisions: {
    id: string; code: string; name: string; colour: string; icon: string; estimateMinutes: number
  }[]
  leadSources: { id: string; name: string; jobs: number }[]
  arrivalMinutes: number
  confirmDefault: boolean
  smsReady: boolean
  company: string | null
  /** The number a confirmation says to ring to change it: the branch's, else the company's (§12.2). */
  phones: { company: string | null; branches: Record<string, string | null> }
  canAddContacts: boolean
  canAddProperties: boolean
  canEditCoverage: boolean
}

export interface FoundPlace {
  id: string; name: string; line1: string; city: string; jobs: number; type: string | null
  /** Tints the property's map mark, as on its list row and its page. */
  zoneColour: string | null
}

export interface FoundPerson {
  id: string
  firstName: string
  lastName: string
  role?: string
  phone: string | null
  phoneKind: 'mobile' | 'office' | 'home' | null
  prefers: 'sms' | 'email' | 'either' | null
  email: string | null
}

export interface FindResult {
  mode: 'digits' | 'email' | 'words' | 'none'
  contacts: {
    total: number
    items: (FoundPerson & { town: string | null; properties: FoundPlace[]; propertyCount: number })[]
  }
  properties: {
    total: number
    items: (FoundPlace & { absorbed: string | null; contacts: FoundPerson[] })[]
  }
  parsed: ParsedTyped
}

export interface Candidate {
  id: string
  name: string
  firstName: string
  recommended: boolean
  soldBefore: boolean
  openRequests: number
  reasons: string[]
  best: Opening | null
}

export interface Recommendation {
  divisionName: string
  branchName: string
  segment: Segment
  covered: number
  teamSize: number
  fallback: boolean
  estimates: boolean
  people: Candidate[]
}

/** Book mode's starting point — `GET /requests/book/:jobId` (§11.4). */
export interface BookContextView {
  jobId: string
  reference: string
  title: string
  status: string
  divisionCode: string | null
  propertyId: string
  contactId: string | null
  appointment: {
    id: string
    description: string
    minutes: number | null
    salespersonId: string | null
    salespersonName: string | null
    startsAt: string | null
  } | null
}

export interface SomeoneElse { id: string; name: string; why: string; covers: boolean }

const q = (params: Record<string, string | number | null | undefined>): string => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') search.set(key, String(value))
  }
  return search.toString()
}

export const requestsApi = {
  setup: () => request<RequestSetup>('/requests/setup'),
  find: (text: string, limit = 6, signal?: AbortSignal) =>
    request<FindResult>(`/requests/find?${q({ q: text, limit })}`, { signal }),
  duplicates: (phone: string | null, email: string | null) =>
    request<{ matches: { contactId: string; name: string; matchedOn: 'email' | 'phone' }[] }>(
      `/requests/duplicates?${q({ phone, email })}`),
  contact: (id: string) => request<KnownContact>(`/requests/contacts/${id}`),
  property: (id: string) => request<KnownProperty>(`/requests/properties/${id}`),
  check: (body: {
    line1: string; line2?: string | null; city: string; state: string; postalCode: string
    latitude?: number | null; longitude?: number | null
  }) => request<PropertyCheck>('/requests/property-check', { method: 'POST', body }),
  salespeople: (params: {
    session: string; orgUnitId: string; division: string; segment: Segment
    lat: number; lng: number; minutes: number; contactId?: string | null
  }) => request<Recommendation>(`/requests/salespeople?${q(params)}`),
  someoneElse: (params: {
    q: string; orgUnitId?: string | null; division?: string | null; segment?: Segment | null
  }) => request<{ items: SomeoneElse[] }>(`/requests/salespeople/search?${q(params)}`),
  calendar: (params: {
    session: string; person: string; lat?: number | null; lng?: number | null; minutes: number
    orgUnitId?: string | null; division?: string | null; segment?: Segment | null
    first?: string | null; day?: string | null; except?: string | null
  }) => request<CalendarView>(`/requests/calendar?${q(params)}`),
  compare: (params: {
    session: string; people: string[]; lat?: number | null; lng?: number | null; minutes: number
    orgUnitId?: string | null; division?: string | null; segment?: Segment | null
    date?: string | null; first?: string | null; except?: string | null
  }) => request<CompareView>(`/requests/compare?${q({ ...params, people: params.people.join(',') })}`),
  bookContext: (jobId: string) => request<BookContextView>(`/requests/book/${jobId}`),
  endSession: (id: string) =>
    request<{ ended: boolean }>(`/requests/session/${id}/end`, { method: 'POST' }),
  create: (body: NewRequestInput) =>
    request<NewRequestResult>('/requests', { method: 'POST', body, idempotencyKey: body.requestId }),
  book: (body: BookRequestInput) =>
    request<BookRequestResult>('/requests/book', { method: 'POST', body, idempotencyKey: body.requestId }),
}
